/**
 * Health Check Redux Slice
 * Manages health check state with Redux Toolkit
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { HealthCheckResponse, HealthCheckState } from '../types/health.types';
import { HealthApiClient } from '../services/healthApi';

// Initial state
const initialState: HealthCheckState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
  autoRefresh: true,
};

// Async thunk for fetching health check data
export const fetchHealthCheck = createAsyncThunk(
  'health/fetchHealthCheck',
  async (_, { rejectWithValue }) => {
    try {
      const data = await HealthApiClient.getHealthCheck();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }
);

// Health check slice
const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetHealthCheck: (state) => {
      state.data = null;
      state.error = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHealthCheck.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthCheck.fulfilled, (state, action: PayloadAction<HealthCheckResponse>) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchHealthCheck.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setAutoRefresh, clearError, resetHealthCheck } = healthSlice.actions;
export default healthSlice.reducer;